import styled from 'styled-components';
import {
  white,
  yellow,
  darkBlueBorder,
  plannerModalBgStart,
  plannerModalBgEnd,
} from '../components/colors';
import { Button } from 'antd';

export const DetailsOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 1000;
  display: ${(props) => (props.$isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
`;

export const DetailsContainer = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, ${plannerModalBgStart} 0%, ${plannerModalBgEnd} 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const DetailsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 36px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
`;

export const DetailsTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  color: ${white};
  text-transform: uppercase;
`;

export const DetailsCloseButton = styled(Button)`
  &.ant-btn {
    background-color: transparent;
    border: none;
    color: ${white};
    font-weight: bold;
    font-size: 16px;
    height: 44px;
    padding: 0 5px;
    border-radius: 8px;

    &:hover {
      background-color: transparent !important;
      border-color: ${yellow} !important;
      color: #000 !important;
    }

    &:focus {
      outline: none;
      background-color: transparent !important;
    }
  }
`;

export const DetailsContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 36px;
  display: flex;
  justify-content: center;
`;

export const DetailsBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 650px;
`;

export const DetailsFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 24px 36px;
  border-top: 2px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
`;

export const EditButton = styled(Button)`
  &.ant-btn {
    background-color: ${yellow};
    border-color: ${yellow};
    color: #000;
    font-weight: bold;
    font-size: 16px;
    height: 50px;
    padding: 0 32px;
    border-radius: 8px;

    &:hover {
      background-color: #ffeb3b !important;
      border-color: #ffeb3b !important;
      color: #000 !important;
    }
  }
`;

export const DetailsDeleteButton = styled(Button)`
  &.ant-btn {
    background-color: #dc3545;
    border: 2px solid #dc3545;
    color: ${white};
    font-weight: bold;
    font-size: 16px;
    height: 50px;
    padding: 0 32px;
    border-radius: 8px;

    &:hover {
      background-color: #c82333 !important;
      border-color: #c82333 !important;
      color: ${white} !important;
    }
  }
`;

export const TypeBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: bold;
  text-transform: uppercase;
  color: ${white};
  background-color: ${(props) => props.$color};
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FieldLabel = styled.span`
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  color: ${yellow};
  letter-spacing: 0.5px;
`;

export const FieldValue = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${white};
  line-height: 1.5;
  white-space: pre-wrap;
`;

export const DateTimeRow = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

export const Divider = styled.div`
  height: 1px;
  background-color: rgba(255, 255, 255, 0.1);
  width: 100%;
`;

export const SectionHeading = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  text-transform: uppercase;
  color: ${white};
  padding-bottom: 8px;
  border-bottom: 2px solid ${yellow};
  display: inline-block;
`;

export const PlayerListReadonly = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const PlayerItemReadonly = styled.div<{ $role?: 'str' | 'sub' }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background-color: ${darkBlueBorder};
  border: 2px solid
    ${(props) =>
      props.$role === 'str'
        ? '#66bb6a'
        : props.$role === 'sub'
          ? '#78909c'
          : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
`;

export const PlayerNumberBadge = styled.div<{ $role?: 'str' | 'sub' }>`
  background-color: ${(props) =>
    props.$role === 'str'
      ? '#66bb6a'
      : props.$role === 'sub'
        ? '#78909c'
        : yellow};
  color: ${(props) => (props.$role ? '#fff' : '#000')};
  font-weight: bold;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
`;

export const PlayerNameReadonly = styled.div`
  display: flex;
  flex-direction: column;
  font-weight: bold;
  font-size: 16px;
  color: ${white};
`;

export const PlayerFlagLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
`;

export const RoleBadge = styled.span<{ $role: 'str' | 'sub' }>`
  margin-left: auto;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  background-color: ${(props) =>
    props.$role === 'str' ? '#66bb6a' : '#78909c'};
  color: #fff;
`;

export const PlayerDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const EmptyState = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;
`;

export const PlayCardList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

export const PlayCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background-color: ${darkBlueBorder};
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s ease;
  width: 160px;

  &:hover {
    border-color: ${yellow};
  }
`;

export const PlayCardPoolWrapper = styled.div`
  width: 130px;
  height: 75px;
  overflow: hidden;
  border-radius: 4px;
  pointer-events: none;
`;

export const PlayCardName = styled.span`
  font-size: 13px;
  font-weight: bold;
  color: ${white};
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;
